import React, { useState } from "react";
import { Accordion, Offcanvas } from "react-bootstrap";
import GlobalAccounts from "./GlobalAccounts";
import styles from "./AccountDetail.module.css";
import {
  lienReportDownload,
  balanceConfirmationDownload,
  emiDeductionWallet,
} from "../../api";
import { Spinner } from "react-bootstrap";
import download from "../../public/images/download.svg";
import Wallets from "./Wallets";
import AccountStatement from "./AccountStatements";
import {
  formatTransferDate,
  formatAmountWithDecimal,
} from "../../../src/Constants/helper";
import TransferStep1 from "./TransferStep1";
import TransferStep2 from "./TransferStep2";
import TransferStep3 from "./TransferStep3";
import { EMI_PURPOSE_OPTIONS } from "../../../src/utils/utils";
import withFeatureActions from "../../withFeatureActions";
import ConversionFlow from "./ConversionFlow";

const UnlienAccountDetails = ({ userId, detail, featureAccess }) => {
  const { writeAccess, modifyAccess } = featureAccess;
  const [activeKey, setActiveKey] = useState("0");
  const [accountId, setAccountId] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const isApproved = accountStatus === "APPROVED";
  const [showEmiDeduction, setShowEmiDeduction] = useState(false);
  const [emiStep, setEmiStep] = useState(1);
  const [emiLoading, setEmiLoading] = useState(false);
  const [emiTransferId, setEmiTransferId] = useState("");
  const [emiTransferOn, setEmiTransferOn] = useState("");
  const [unlienBalance, setUnlienBalance] = useState(0);
  const [walletUpdate, setWalletUpdate] = useState(0);
  const [showConversion, setShowConversion] = useState(false);
  const [hasBalance, setHasBalance] = useState(false);

  const [emiFormData, setEmiFormData] = useState({
    amount: "",
    remarks: "",
    purpose: "",
  });

  const handleToggle = (key) => {
    setActiveKey(activeKey === key ? null : key);
  };

  const downloadPdf = async () => {
    setGlobalLoading(true);
    try {
      const payload = {
        accountStatementType: "GENERAL",
        accountId: accountId,
      };

      const resp = await lienReportDownload(payload);

      if (resp?.status === 200 && resp?.data) {
        const blob = new Blob([resp.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "AccountOpeningLetter.pdf";
        link.click();

        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Download Error:", error);
    }
    setGlobalLoading(false);
  };
  const openEmiOffcanvas = () => {
    setEmiStep(1);
    setEmiFormData({
      amount: "",
      remarks: "",
      purpose: "",
    });
    setEmiTransferId("");
    setEmiTransferOn("");
    setShowEmiDeduction(true);
  };
  const closeEmiOffcanvas = () => {
    setShowEmiDeduction(false);
    setTimeout(() => setEmiStep(1), 300);
  };
  const handleEmiStep1Next = ({ amount, remarks }) => {
    setEmiFormData((prev) => ({
      ...prev,
      amount,
      remarks,
    }));

    setEmiTransferOn(formatTransferDate());
    setEmiStep(2);
  };
  const handleEmiConfirm = async () => {
    try {
      setEmiLoading(true);

      const payload = {
        userId,
        remarks: emiFormData.remarks,
        currency: "USD",
        amount: parseFloat(emiFormData.amount),
        purpose: emiFormData.purpose,
      };

      const res = await emiDeductionWallet(payload);

      if (res?.status === 200) {
        const resData = res?.data?.data;

        setEmiTransferId(resData?.transactionNo || "");
        setEmiTransferOn(formatTransferDate(new Date(resData?.createdAt)));
        setWalletUpdate((prev) => prev + 1);
        setEmiStep(3);
      }
    } finally {
      setEmiLoading(false);
    }
  };

  const balanceConfirmationDownloadPdf = async () => {
    setBalanceLoading(true);
    try {
      const resp = await balanceConfirmationDownload(userId, "GLOBAL");

      if (resp?.status === 200 && resp?.data) {
        const blob = new Blob([resp.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "BalanceConfirmation.pdf";
        link.click();

        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Download Error:", error);
    }
    setBalanceLoading(false);
  };

  return (
    <div className={styles.accountDetailContainer}>
      <Accordion activeKey={activeKey} className={styles.accordionContainer}>
        <Accordion.Item eventKey="0">
          <Accordion.Header
            onClick={() => handleToggle("0")}
            className={styles.accordionHeader}
          >
            <div className={styles.HeaderButton}>
              <span>Global Account</span>
              <div>
                {writeAccess && accountId && accountStatus === "APPROVED" && (
                  <span
                    className={styles.DownloadButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadPdf();
                    }}
                  >
                    {globalLoading ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        className={styles.spinnerButton}
                      />
                    ) : (
                      <img
                        src={download}
                        alt="download"
                        className={styles.iconButton}
                      />
                    )}
                    Account Opening Letter
                  </span>
                )}
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body className={styles.accordionBody}>
            <GlobalAccounts
              userId={userId}
              onAccountId={(id) => setAccountId(id)}
              onAccountStatus={(s) => setAccountStatus(s)}
            />
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="1" style={{ marginTop: "20px" }}>
          <Accordion.Header
            onClick={() => handleToggle("1")}
            className={styles.accordionHeader}
          >
            <div className={styles.HeaderButton}>
              <span>Wallets</span>
              <div>
                {writeAccess && isApproved && (
                  <span
                    className={styles.DownloadButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      balanceConfirmationDownloadPdf();
                    }}
                  >
                    {balanceLoading ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        className={styles.spinnerButton}
                      />
                    ) : (
                      <img
                        src={download}
                        alt="download"
                        className={styles.iconButton}
                      />
                    )}
                    Balance Confirmation Letter
                  </span>
                )}
                {modifyAccess && isApproved && hasBalance && (
                  <span
                    className={styles.DownloadButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowConversion(true);
                    }}
                  >
                    Conversion
                  </span>
                )}
                {modifyAccess && isApproved && (
                  <span
                    className={styles.DownloadButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      openEmiOffcanvas();
                    }}
                  >
                    EMI Deduction
                  </span>
                )}
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body className={styles.accordionBody}>
            <Wallets
              userId={userId}
              accountStatus={accountStatus}
              onBalanceChange={(balance) => setUnlienBalance(balance)}
              onHasBalance={setHasBalance}
              refreshKey={walletUpdate}
            />{" "}
          </Accordion.Body>
        </Accordion.Item>
        {writeAccess && isApproved && (
          <Accordion.Item eventKey="2" style={{ marginTop: "20px" }}>
            <Accordion.Header
              onClick={() => handleToggle("2")}
              className={styles.accordionHeader}
            >
              Account Statement
            </Accordion.Header>
            <Accordion.Body className={styles.accordionBody}>
              <AccountStatement
                userId={userId}
                accountType="GLOBAL"
                isActive={activeKey === "2"}
              />
            </Accordion.Body>
          </Accordion.Item>
        )}
      </Accordion>
      <Offcanvas
        show={showEmiDeduction}
        onHide={closeEmiOffcanvas}
        placement="end"
        className={styles.offcanvas}
        style={{
          color: "white",
          backgroundColor: "#28282B",
          width: "650px",
        }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ color: "white" }}>
            EMI Deduction
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body style={{ padding: 0 }}>
          {emiStep === 1 && (
            <TransferStep1
              lienBalance={unlienBalance}
              userName={detail?.name || ""}
              onNext={handleEmiStep1Next}
              onCancel={closeEmiOffcanvas}
              formData={emiFormData}
              setFormData={setEmiFormData}
              transferType="EMI"
              purposeOptions={EMI_PURPOSE_OPTIONS}
            />
          )}

          {emiStep === 2 && (
            <TransferStep2
              amount={emiFormData.amount}
              remarks={emiFormData.remarks}
              name={detail?.name || ""}
              transferPurpose={emiFormData.purpose}
              transferOn={emiTransferOn}
              onBack={() => setEmiStep(1)}
              onConfirm={handleEmiConfirm}
              loading={emiLoading}
              transferType="EMI"
            />
          )}

          {emiStep === 3 && (
            <TransferStep3
              transferId={emiTransferId}
              amount={emiFormData.amount}
              remarks={emiFormData.remarks}
              name={detail?.name || ""}
              transferPurpose={emiFormData.purpose}
              transferOn={emiTransferOn}
              transferType="EMI"
            />
          )}
        </Offcanvas.Body>
          </Offcanvas>
          <Offcanvas
            show={showConversion}
            onHide={() => setShowConversion(false)}
            placement="end"
            className={styles.offcanvas}
            style={{ color: "white", backgroundColor: "#28282B", width: "650px" }}
          >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title style={{ color: "white" }}>Conversion</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body style={{ padding: 0 }}>
            <ConversionFlow
              userId={userId}
              onClose={() => {
                setShowConversion(false);
                setWalletUpdate((prev) => prev + 1); // refresh wallet balances after conversion
              }}
            />
        </Offcanvas.Body>
    </Offcanvas>
    </div>
  );
};
export default withFeatureActions(
  UnlienAccountDetails,
  "MCB",
  "Unlien Account Details",
);
